import { useCallback, useEffect, useMemo, useState } from 'react'
import Attachments from '../components/Attachments.jsx'
import Sheet from '../components/Sheet.jsx'
import {
  KINDS, addEntry, addSource, deleteEntry, depositIncome, getDashboard, getDeposit,
  getEntries, monthRange, toCsv, updateEntry,
} from '../lib/income.js'
import {
  currentMonthKey, monthName, parseAmount, peso, shiftMonthKey, shortDate, todayISO,
} from '../lib/format.js'
import { canReceive, getAccounts } from '../lib/worth.js'

/**
 * Everything coming in, from every gig, typed by him. A dashboard on top and a
 * ledger underneath that behaves like a spreadsheet.
 *
 * The number at the top always matches exactly what is listed below it. Every
 * filter runs in the query rather than after it, so the two cannot drift.
 */
export default function Income({ toast }) {
  const [monthKey, setMonthKey] = useState(currentMonthKey())
  const [dash, setDash] = useState(null)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  const [filter, setFilter] = useState(() => {
    const [from, to] = monthRange(currentMonthKey())
    return { from, to, sourceId: '', min: '', max: '', text: '' }
  })

  const load = useCallback(async () => {
    setError(null)
    try {
      const [d, r] = await Promise.all([
        getDashboard(monthKey),
        getEntries({
          from: filter.from || null,
          to: filter.to || null,
          sourceId: filter.sourceId || null,
          min: parseAmount(filter.min) || null,
          max: parseAmount(filter.max) || null,
          text: filter.text || null,
        }),
      ])
      setDash(d)
      setRows(r)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [monthKey, filter])

  useEffect(() => { load() }, [load])

  const sources = dash?.sources ?? []
  const byId = useMemo(
    () => Object.fromEntries(sources.map((s) => [s.id, s])),
    [sources],
  )
  const listed = rows.reduce((s, r) => s + Number(r.amount || 0), 0)

  const filtered =
    filter.sourceId || filter.min || filter.max || filter.text ||
    filter.from !== monthRange(monthKey)[0] || filter.to !== monthRange(monthKey)[1]

  function goMonth(delta) {
    const next = shiftMonthKey(monthKey, delta)
    const [from, to] = monthRange(next)
    setMonthKey(next)
    setFilter((f) => ({ ...f, from, to }))
  }

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your income. {error}</p>
      </div>
    )
  }
  if (!dash) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  return (
    <>
      <div className="page-head month">
        <div className="monthnav">
          <button className="navbtn" type="button" aria-label="Previous month"
            onClick={() => goMonth(-1)}>‹</button>
          <b>{monthName(monthKey)}</b>
          <button className="navbtn" type="button" aria-label="Next month"
            onClick={() => goMonth(1)}>›</button>
        </div>
      </div>

      <div className="card">
        <div className="pad">
          <span className="lbl">In this month</span>
          <div className="in-total">
            <span className="num serif">{peso(dash.total)}</span>
            {dash.change === null ? (
              <span className="lbl">no last month</span>
            ) : (
              <span className={'delta ' + (dash.change >= 0 ? 'up' : 'down')}>
                {dash.change >= 0 ? '+' : ''}{dash.change}%
              </span>
            )}
          </div>
          <p className="lbl" style={{ margin: '2px 0 0' }}>
            {peso(dash.lastTotal)} last month
          </p>
        </div>

        {dash.total > 0 ? (
          <div className="pad" style={{ borderTop: '1px solid var(--rule-soft)' }}>
            <span className="lbl">Salary vs everything else</span>
            <div className="rail" style={{ margin: '8px 0 7px' }}>
              <i style={{ width: dash.salaryPct + '%', background: 'var(--notes)' }} />
              <i style={{ width: 100 - dash.salaryPct + '%', background: 'var(--health)' }} />
            </div>
            <div className="split-key">
              <span><i style={{ background: 'var(--notes)' }} />Salary {peso(dash.salary)}</span>
              <span><i style={{ background: 'var(--health)' }} />Everything else {peso(dash.other)}</span>
            </div>
          </div>
        ) : null}
      </div>

      {dash.ranked.length ? (
        <>
          <p className="lbl" style={{ margin: '20px 2px 8px' }}>By source</p>
          <div className="card">
            <div className="pad">
              {dash.ranked.map((s) => (
                <div className="srcrow" key={s.id}>
                  <div className="srchead">
                    <b>{s.name}</b>
                    <span className="num">{peso(s.amount)}</span>
                  </div>
                  <div className="rail">
                    <i style={{ width: Math.max(2, s.pct) + '%', background: 'var(--ocean)' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="ledger-head">
        <div>
          <span className="lbl">{filtered ? 'Filtered' : 'This month'}</span>
          <b className="num">{peso(listed)}</b>
          <span className="lbl">{rows.length} {rows.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        <button type="button" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Hide filters' : 'Filter'}
        </button>
      </div>

      {showFilters ? (
        <div className="card">
          <div className="pad">
            <div className="frow">
              <div className="field">
                <label className="lbl" htmlFor="f-from">From</label>
                <input id="f-from" type="date" value={filter.from}
                  onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="field">
                <label className="lbl" htmlFor="f-to">To</label>
                <input id="f-to" type="date" value={filter.to}
                  onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))} />
              </div>
            </div>
            <div className="field">
              <label className="lbl" htmlFor="f-src">Source</label>
              <select id="f-src" value={filter.sourceId}
                onChange={(e) => setFilter((f) => ({ ...f, sourceId: e.target.value }))}>
                <option value="">Every source</option>
                {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="frow">
              <div className="field">
                <label className="lbl" htmlFor="f-min">At least</label>
                <input id="f-min" inputMode="numeric" value={filter.min}
                  onChange={(e) => setFilter((f) => ({ ...f, min: e.target.value }))} />
              </div>
              <div className="field">
                <label className="lbl" htmlFor="f-max">At most</label>
                <input id="f-max" inputMode="numeric" value={filter.max}
                  onChange={(e) => setFilter((f) => ({ ...f, max: e.target.value }))} />
              </div>
            </div>
            <div className="field">
              <label className="lbl" htmlFor="f-text">Note contains</label>
              <input id="f-text" value={filter.text}
                onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))} />
            </div>
            <div className="sheet-actions" style={{ marginTop: 4 }}>
              <button type="button" className="btn ghost" onClick={() => {
                const [from, to] = monthRange(monthKey)
                setFilter({ from, to, sourceId: '', min: '', max: '', text: '' })
              }}>Clear</button>
              <button type="button" className="btn ghost" onClick={() => {
                /* The file is built from what is on screen, so it can never
                   disagree with the total above it. */
                const csv = toCsv(rows, sources)
                const blob = new Blob([csv], { type: 'text/csv' })
                const a = document.createElement('a')
                a.href = URL.createObjectURL(blob)
                a.download = 'income-' + monthKey + '.csv'
                a.click()
                URL.revokeObjectURL(a.href)
              }}>Export CSV</button>
            </div>
          </div>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="empty">
          <h2 className="serif">{filtered ? 'Nothing matches' : 'Nothing logged yet'}</h2>
          <p>
            {filtered
              ? 'Widen the filters, or clear them.'
              : 'Log what arrived and where it came from. Salary, gigs, the businesses.'}
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="rows">
            {rows.map((e) => {
              const s = byId[e.source_id]
              return (
                <button className="row" key={e.id} type="button"
                  onClick={() => setSheet({ kind: 'edit', entry: e })}>
                  <div className="nm">
                    <b>{s?.name || 'Unknown source'}</b>
                    <span className="lbl">
                      {shortDate(e.received_on)}{e.note ? ' · ' + e.note : ''}
                    </span>
                  </div>
                  {s?.kind === 'salary' ? <span className="tag build">SALARY</span> : null}
                  <span className="amt num">{peso(e.amount)}</span>
                  <span className="go" aria-hidden="true">›</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <button type="button" className="btn" style={{ marginTop: 14 }}
        onClick={() => setSheet({ kind: 'new' })}>
        Log income
      </button>

      {sheet?.kind === 'new' || sheet?.kind === 'edit' ? (
        <EntrySheet
          entry={sheet.entry}
          sources={sources}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}
    </>
  )
}

/**
 * Source is a datalist, not a dropdown: he types, it completes from what he
 * has used before, and a name it has never seen becomes a new source once he
 * picks its kind. Nothing is created behind his back.
 */
function EntrySheet({ entry, sources, onClose, onSaved, toast }) {
  const existing = entry ? sources.find((s) => s.id === entry.source_id) : null
  const [name, setName] = useState(existing?.name ?? '')
  const [kind, setKind] = useState(existing?.kind ?? 'gig')
  const [amount, setAmount] = useState(entry ? String(Math.round(Number(entry.amount))) : '')
  const [on, setOn] = useState(entry?.received_on ?? todayISO())
  const [note, setNote] = useState(entry?.note ?? '')
  const [busy, setBusy] = useState(false)
  /* Where the money went, if anywhere. Blank is plain income, as before. */
  const [accounts, setAccounts] = useState([])
  const [depositTo, setDepositTo] = useState('')
  const [deposit, setDeposit] = useState(undefined)
  const [depositing, setDepositing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [reverse, setReverse] = useState(true)

  useEffect(() => {
    let alive = true
    getAccounts().then((a) => { if (alive) setAccounts(a.filter(canReceive)) }).catch(() => {})
    if (entry?.id) {
      getDeposit(entry.id)
        .then((d) => { if (alive) setDeposit(d) })
        .catch(() => { if (alive) setDeposit(null) })
    }
    return () => { alive = false }
  }, [entry?.id])

  const match = sources.find((s) => s.name.toLowerCase() === name.trim().toLowerCase())
  const isNew = name.trim().length > 0 && !match

  return (
    <Sheet title={entry ? 'Edit income' : 'Log income'} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          const amt = parseAmount(amount)
          if (busy || !amt || !name.trim()) return
          setBusy(true)
          try {
            const sourceId = match ? match.id : (await addSource({ name, kind })).id
            if (entry) {
              await updateEntry(entry.id, {
                source_id: sourceId, amount: amt, received_on: on, note: note.trim() || null,
              })
            } else {
              const created = await addEntry({ sourceId, amount: amt, receivedOn: on, note })
              const account = accounts.find((a) => a.id === depositTo)
              if (account) {
                try {
                  await depositIncome(created, account, amt, name.trim())
                  toast('Logged, and ' + peso(amt) + ' added to ' + account.name + '.')
                } catch (err) {
                  /* The income is real either way. Say plainly that the
                     balance did not move so he can retry from the entry. */
                  toast('Income logged, but the deposit failed: ' + (err.message || 'try again from the entry.'))
                }
              }
            }
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="i-src">Where it came from</label>
          <input id="i-src" list="src-list" required autoComplete="off"
            value={name} onChange={(e) => setName(e.target.value)} />
          <datalist id="src-list">
            {sources.map((s) => <option key={s.id} value={s.name} />)}
          </datalist>
        </div>

        {isNew ? (
          <div className="field">
            <label className="lbl" htmlFor="i-kind">New source — what kind?</label>
            <select id="i-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>
        ) : null}

        <div className="field">
          <label className="lbl" htmlFor="i-amt">Amount</label>
          <input id="i-amt" inputMode="numeric" placeholder="0" required
            value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="i-on">Received</label>
          <input id="i-on" type="date" required value={on} onChange={(e) => setOn(e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="i-note">Note</label>
          <input id="i-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        {!entry && accounts.length ? (
          <div className="field">
            <label className="lbl" htmlFor="i-dep">Deposit to</label>
            <select id="i-dep" value={depositTo} onChange={(e) => setDepositTo(e.target.value)}>
              <option value="">Don't deposit</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.institution ? ' · ' + a.institution : ''}
                </option>
              ))}
            </select>
            {depositTo ? (
              <p className="hint">Adds the amount to this account and records it in Net worth.</p>
            ) : null}
          </div>
        ) : null}

        {entry && deposit ? (
          <p className="hint" style={{ margin: '0 2px 14px' }}>
            Deposited {peso(deposit.amount)} to {deposit.account.name}. Changing the amount
            here does not change the deposit.
          </p>
        ) : null}

        {/* The screenshot of the transfer, kept with the entry that records
            it rather than in a camera roll he has to go searching through. */}
        {entry?.id ? (
          <Attachments target={{ type: 'income_entry', id: entry.id }} toast={toast} compact />
        ) : null}

        <button className="btn" type="submit" data-sheet-primary={!entry || undefined}
          disabled={busy || !parseAmount(amount) || !name.trim()}>
          {busy ? 'Saving…' : entry ? 'Save' : 'Log it'}
        </button>

        {entry && deposit === null && accounts.length ? (
          <button type="button" className="btn ghost" style={{ marginTop: 10 }}
            onClick={() => setDepositing(true)}>
            Deposit to an account
          </button>
        ) : null}

        {entry && !confirmingDelete ? (
          <button type="button" className="btn ghost" style={{ marginTop: 10 }}
            onClick={() => setConfirmingDelete(true)}>
            Remove this entry
          </button>
        ) : null}

        {entry && confirmingDelete ? (
          <div className="notice warn" style={{ marginTop: 12 }}>
            <p style={{ margin: '0 0 8px', fontSize: 14 }}>Remove this income entry?</p>
            {deposit ? (
              <label className="checkline">
                <input type="checkbox" checked={reverse}
                  onChange={(e) => setReverse(e.target.checked)} />
                <span>Also take {peso(deposit.amount)} back out of {deposit.account.name}</span>
              </label>
            ) : null}
            <div className="sheet-actions" style={{ marginTop: 10 }}>
              <button type="button" className="btn ghost" onClick={() => setConfirmingDelete(false)}>
                Keep
              </button>
              <button type="button" className="btn danger" disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await deleteEntry(entry.id, { reverse: deposit && reverse ? deposit : null })
                    onSaved()
                  } catch (err) {
                    toast(err.message || 'Could not remove that.')
                    setBusy(false)
                  }
                }}>
                Remove
              </button>
            </div>
          </div>
        ) : null}
      </form>

      {depositing ? (
        <DepositSheet
          entry={entry}
          sourceName={existing?.name}
          accounts={accounts}
          toast={toast}
          onClose={() => setDepositing(false)}
          onDone={() => { setDepositing(false); onSaved() }}
        />
      ) : null}
    </Sheet>
  )
}

/* For income logged before deposits existed, or logged without one. Straight
   into the account: no payout line, because payouts are the budget. */
function DepositSheet({ entry, sourceName, accounts, toast, onClose, onDone }) {
  const [amount, setAmount] = useState(String(Math.round(Number(entry.amount) || 0)))
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? '')
  const [busy, setBusy] = useState(false)
  const value = parseAmount(amount)

  return (
    <Sheet title="Deposit to an account" onClose={onClose}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ink-dim)' }}>
        {peso(entry.amount)} from {sourceName || 'this entry'} on {shortDate(entry.received_on)}.
      </p>
      <div className="field">
        <label className="lbl" htmlFor="dep-amt">How much</label>
        <input id="dep-amt" inputMode="numeric" value={amount}
          onChange={(e) => setAmount(e.target.value)} />
      </div>
      <div className="field">
        <label className="lbl" htmlFor="dep-acct">Into</label>
        <select id="dep-acct" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}{a.institution ? ' · ' + a.institution : ''}
            </option>
          ))}
        </select>
      </div>
      <button className="btn" type="button" disabled={busy || !value || !accountId}
        onClick={async () => {
          const account = accounts.find((a) => a.id === accountId)
          if (busy || !value || !account) return
          setBusy(true)
          try {
            await depositIncome(entry, account, value, sourceName)
            toast(peso(value) + ' added to ' + account.name + '.')
            onDone()
          } catch (err) {
            toast(err.message || 'Could not deposit that.')
            setBusy(false)
          }
        }}>
        {busy ? 'Depositing…' : 'Deposit'}
      </button>
    </Sheet>
  )
}
